<template>
  <v-expansion-panels v-if="!_.isEmpty(survey)">
    <v-expansion-panel
      :readonly="
        (survey.sentInstancesCount == 0 &&
          survey.scheduledInstancesCount == 0) ||
          disableExpansion
      "
      class="rounded-lg overflow-hidden card-border"
    >
      <v-expansion-panel-header
        :class="`py-0`"
        :hide-actions="
          (survey.sentInstancesCount == 0 &&
            survey.scheduledInstancesCount == 0) ||
            disableExpansion
        "
      >
        <template v-slot:default="{}">
          <v-row>
            <v-col
              :cols="$vuetify.breakpoint.mdAndUp ? 'auto' : '12'"
              :class="{
                'my-auto': true,
                'pb-0': $vuetify.breakpoint.smAndDown
              }"
            >
              <v-chip
                :color="getSurveyStatusColor(survey)"
                :dark="isSurveyStatusDark(survey)"
              >
                {{ getSurveyStatusText(survey) }}
              </v-chip>
            </v-col>
            <v-col :class="$vuetify.breakpoint.mdAndUp ? 'py-6' : ''">
              <v-row dense>
                <v-col cols="auto" class="text-h6 secondary--text">
                  {{ survey.title }}
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        :to="'/features/surveys/' + survey.id + '/builder'"
                        v-if="survey.canSave && !survey.isComplete"
                        color="lighttext"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small>edit</v-icon>
                      </v-btn>
                    </template>
                    <div class="tooltip-limit-width">
                      Edit
                    </div>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        @click="$emit('duplicate')"
                        color="lighttext"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small>content_copy</v-icon>
                      </v-btn>
                    </template>
                    <div class="tooltip-limit-width">
                      Duplicate
                    </div>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        @click="$emit('archive')"
                        v-if="
                          !survey.isComplete &&
                            survey.sentInstancesCount > 0 &&
                            survey.canSave
                        "
                        color="lighttext"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small>archive</v-icon>
                      </v-btn>
                    </template>
                    <div class="tooltip-limit-width">
                      Archive
                    </div>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-if="
                          survey.questionCount > 0 &&
                            !survey.isComplete &&
                            survey.previewLink
                        "
                        icon
                        small
                        :href="survey.previewLink"
                        target="_blank"
                        color="lighttext"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small>search</v-icon>
                      </v-btn>
                    </template>
                    <div class="tooltip-limit-width">
                      Preview
                    </div>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-if="survey.sentInstancesCount == 0 && survey.canSave"
                        icon
                        small
                        color="lighttext"
                        @click="$emit('delete')"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small>delete</v-icon>
                      </v-btn>
                    </template>
                    <div class="tooltip-limit-width">
                      Delete
                    </div>
                  </v-tooltip>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="auto" class="lighttext--text">
                  Created: {{ survey.createdDate | normalDate }} | By
                  {{ survey.createdBy.fullName }} |
                  {{ survey.questionCount }} Questions
                  <template
                    v-if="
                      survey.userRecords &&
                        survey.userRecords.finished > 0 &&
                        !disableExpansion
                    "
                    >|
                    <router-link
                      :to="`/reporting/surveys/overview/surveys/${survey.id}`"
                      class="info--text"
                      >{{ survey.userRecords.finished }} Responses</router-link
                    >
                  </template>
                </v-col>
              </v-row>
            </v-col>
            <v-spacer class="limited-spacer"></v-spacer>
            <inline-svg
              :width="100"
              :height="100"
              :fill="$vuetify.theme.themes.light.lighttext"
              class="badge-background"
              :src="survey.badgeUrl"
              v-if="survey.badgeUrl"
            />
            <v-col
              cols="auto"
              class="my-auto"
              v-if="
                survey.questionCount > 0 &&
                  survey.sentInstancesCount == 0 &&
                  survey.scheduledInstancesCount == 0 &&
                  !survey.isComplete
              "
            >
              <v-btn color="lighttext" outlined @click="showSchedule(null)">
                <v-icon color="secondary">send</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </v-expansion-panel-header>
      <v-expansion-panel-content class="pa-0">
        <v-row
          class="text-center justify-space-around"
          v-if="
            survey.sentInstancesCount > 0 || survey.scheduledInstancesCount > 0
          "
        >
          <v-col cols="12" sm="6" md="3">
            <v-card outlined height="100%" class="d-flex">
              <v-card-text class="my-auto">
                <v-avatar
                  color="lighttext"
                  class="mr-2 text-h6 font-weight-medium"
                >
                  {{ survey.sentInstancesCount }}
                </v-avatar>
                Unique Sends
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card outlined height="100%" class="d-flex">
              <v-card-text class="my-auto">
                <engagement-text
                  title="Participation Rate"
                  :engagement="
                    survey.userRecords || {
                      finished: 0,
                      sent: 0,
                      completion: 0
                    }
                  "
                ></engagement-text>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card
              outlined
              height="100%"
              class="d-flex"
              :disabled="survey.sentInstancesCount == 0"
              :to="`/reporting/surveys/overview/surveys/${survey.id}`"
            >
              <v-card-text class="my-auto secondary--text">
                <v-icon class="mr-2" color="secondary">insert_chart</v-icon>
                View Responses
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3" v-if="!survey.isComplete">
            <v-card
              outlined
              height="100%"
              class="d-flex"
              @click="showSchedule(null)"
            >
              <v-card-text class="my-auto secondary--text">
                <v-icon class="mr-2" color="secondary">send</v-icon>
                Schedule Send
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <survey-card-instances
          :survey="survey"
          v-on:edit="showSchedule"
          v-on:delete="deleteInstance"
          v-on:close="closeInstance"
          v-on:report="showReport"
          v-on:viewlink="showLiveLink"
          v-on:addRecipients="showAddRecipients"
        >
        </survey-card-instances>
      </v-expansion-panel-content>
    </v-expansion-panel>
    <survey-schedule-dialog
      ref="scheduleDialog"
      v-on:save="loadInstances"
    ></survey-schedule-dialog>
    <survey-add-recipients-dialog
      ref="addRecipientsDialog"
      v-on:save="loadInstances"
    ></survey-add-recipients-dialog>
    <survey-live-link-dialog ref="linkDialog"></survey-live-link-dialog>
  </v-expansion-panels>
</template>

<script>
import surveyStatuses from "@/helpers/mixins/surveyStatuses";
import SurveyScheduleDialog from "@/components/surveys/SurveyScheduleDialog";
import SurveyAddRecipientsDialog from "@/components/surveys/SurveyAddRecipientsDialog";
import SurveyLiveLinkDialog from "@/components/surveys/SurveyLiveLinkDialog";
import SurveyCardInstances from "@/components/surveys/SurveyCardInstances";
import EngagementText from "@/components/reporting/EngagementText";
import InlineSvg from "vue-inline-svg";
export default {
  name: "SurveyCard",
  components: {
    SurveyScheduleDialog,
    SurveyAddRecipientsDialog,
    SurveyLiveLinkDialog,
    EngagementText,
    SurveyCardInstances,
    InlineSvg
  },
  mixins: [surveyStatuses],
  props: {
    survey: { type: Object },
    disableExpansion: { type: Boolean, default: false }
  },
  computed: {},
  methods: {
    loadInstances() {
      this.$store.dispatch("survey/getSurveyInstances", this.survey.id);
    },
    showSchedule(instanceId) {
      this.$refs.scheduleDialog.showDialog(this.survey.id, instanceId);
    },
    showAddRecipients(instanceId) {
      this.$refs.addRecipientsDialog.showDialog(this.survey.id, instanceId);
    },
    showReport(instanceId) {
      this.$emit("report", {
        surveyId: this.survey.id,
        instanceIds: instanceId ? [instanceId] : []
      });
    },
    showLiveLink(instanceId) {
      this.$refs.linkDialog.showDialog(this.survey.id, instanceId);
    },
    async deleteInstance(instanceId) {
      if (
        await this.$root.$confirm(
          "Delete scheduled survey",
          "Are you sure you want to delete this survey send?",
          { color: "red" }
        )
      ) {
        this.$store
          .dispatch("survey/deleteSurveyInstance", {
            surveyId: this.survey.id,
            id: instanceId
          })
          .then(() => {
            this.$root.$snackbarSuccess("Survey send deleted");
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occured while deleting this survey send."
            );
          });
      }
    },
    async closeInstance(instanceId) {
      if (
        await this.$root.$confirm(
          "Close survey send",
          "Are you sure you want to close this survey? Once closed, this survey send will not be able to be answered.",
          { color: "red" }
        )
      ) {
        this.$store
          .dispatch("survey/closeSurveyInstance", {
            surveyId: this.survey.id,
            id: instanceId
          })
          .then(() => {
            this.$emit("close");
            this.$root.$snackbarSuccess("Survey send closed");
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occured while closing this survey send."
            );
          });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.v-chip {
  min-width: 100px;

  ::v-deep .v-chip__content {
    margin-left: auto;
    margin-right: auto;
  }
}

::v-deep .v-expansion-panel-header__icon i {
  color: var(--v-secondary-base) !important;
}

.badge-background {
  position: absolute;
  bottom: -50px;
  right: 65px;
}

::v-deep .cls-1 {
  fill: inherit !important;
}
</style>
